import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import api from '../../services/api';

const CronManagement = ({ isOpen, onClose }) => {
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState({});
  const [activeTab, setActiveTab] = useState('tasks');
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  // Fetch cron information
  const fetchCronInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('cron/info');
      console.log('Cron API Response:', response.data);
      if (response.data.status === 1) {                     
        const tasks = response.data.data.tasks || [];
        const logs = response.data.data.recent_logs || [];
        console.log('Tasks:', tasks);
        console.log('Logs:', logs);
        setTasks(tasks);
        setLogs(logs);
      } else {
        setError('Failed to fetch cron information');
      }
    } catch (error) {
      console.error('Error fetching cron info:', error);
      setError(error.response?.data?.message || 'Failed to load cron information');
    } finally {
      setLoading(false);
    }
  };

  // Execute specific cron task
  const executeCronTask = async (taskId) => {
    setExecuting(prev => ({ ...prev, [taskId]: true }));
    setError(null);
    
    try {
      const response = await api.post('cron/trigger', { task: taskId });
      
      // Handle response - may contain debug URLs from Microsoft Graph API
      // Extract clean JSON if response is a string with mixed content
      let responseData = response.data;
      if (typeof responseData === 'string') {
        // Find the last JSON object in the response
        const jsonStart = responseData.lastIndexOf('{');
        if (jsonStart !== -1) {
          try {
            responseData = JSON.parse(responseData.substring(jsonStart));
          } catch (e) {
            console.warn('Failed to parse JSON from response, using original');
          }
        }
      }
      
      if (responseData.status === 1) {
        // Show success message with SweetAlert2
        const task = tasks.find(t => t.id === taskId);
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          html: `<strong>${task?.name || taskId}</strong> executed successfully<br/><small>Duration: ${responseData.duration_ms || '0'}ms</small>`,
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
        
        // Refresh logs
        await fetchCronInfo();
      } else {
        throw new Error(responseData.message || 'Execution failed');
      }
    } catch (error) {
      console.error('Error executing cron:', error);
      setError(error.response?.data?.message || `Failed to execute ${taskId}`);
      
      // Show error message with SweetAlert2
      await Swal.fire({
        icon: 'error',
        title: 'Execution Failed',
        text: error.response?.data?.message || 'Execution failed',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setExecuting(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Get last run time for a specific task from logs
  const getLastRunTime = (taskId) => {
    // Find the most recent log entry for this task
    const taskLog = logs.find(log => {
      if (!log.context) return false;
      const context = typeof log.context === 'string' ? JSON.parse(log.context) : log.context;
      return context.task === taskId && (
        context.operation === 'cron_manual_complete' || 
        context.operation === 'cron_manual_trigger'
      );
    });

    if (taskLog) {
      const timestamp = new Date(taskLog.timestamp);
      const now = new Date();
      const diffMs = now - timestamp;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    }

    return 'Never';
  };

  // Filter logs based on search and level
  const filteredLogs = logs.filter(log => {
    // Filter by level
    if (filterLevel !== 'all' && log.level !== filterLevel) {
      return false;
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const message = log.message?.toLowerCase() || '';
      const contextStr = JSON.stringify(log.context || {}).toLowerCase();
      return message.includes(searchLower) || contextStr.includes(searchLower);
    }

    return true;
  });

  // Auto-fetch on mount and when tab changes
  useEffect(() => {
    if (isOpen) {
      fetchCronInfo();
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(fetchCronInfo, 30000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Get log level badge color
  const getLevelBadge = (level) => {
    const colors = {
      emergency: 'bg-red-900 text-white',
      alert: 'bg-red-700 text-white',
      critical: 'bg-red-600 text-white',
      error: 'bg-red-500 text-white',
      warning: 'bg-orange-500 text-white',
      notice: 'bg-blue-500 text-white',
      info: 'bg-green-500 text-white',
      debug: 'bg-gray-500 text-white'
    };
    return colors[level] || 'bg-gray-400 text-white';
  };

  // Get module badge color
  const getModuleBadge = (module) => {
    const colors = {
      'Notices': 'bg-blue-100 text-blue-800',
      'Procurement': 'bg-purple-100 text-purple-800',
      'Water Management': 'bg-cyan-100 text-cyan-800',
      'Asset Management': 'bg-orange-100 text-orange-800',
      'Helpdesk': 'bg-green-100 text-green-800',
      'System': 'bg-gray-100 text-gray-800'
    };
    return colors[module] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Compact Header */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 rounded-lg p-2" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Cron Job Management</h2>
              <p className="text-xs text-gray-500">Monitor and control scheduled tasks</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Compact Tabs */}
        <div className="px-6 border-b border-gray-200 bg-white">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-3 py-2 font-medium text-xs transition-colors border-b-2 ${
                activeTab === 'tasks'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Tasks
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-1.5 py-0.5 rounded">
                  {tasks.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-3 py-2 font-medium text-xs transition-colors border-b-2 ${
                activeTab === 'logs'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Logs
                <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-1.5 py-0.5 rounded">
                  {logs.length}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Compact Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm text-gray-600">Loading cron information...</p>
            </div>
          ) : (
            <>
              {/* Tasks Tab - 2 Column Grid */}
              {activeTab === 'tasks' && (
                <div>
                  {tasks.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-gray-600 font-medium">No cron tasks configured</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {tasks.map(task => {
                        const lastRun = getLastRunTime(task.id);
                        return (
                          <div
                            key={task.id}
                            className="bg-white border border-gray-200 rounded-lg p-2.5 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex flex-col gap-1.5">
                              {/* Header with badges */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-xs font-semibold text-gray-900 mb-1">{task.name}</h3>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getModuleBadge(task.module)}`}>
                                      {task.module}
                                    </span>
                                    {task.enabled ? (
                                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                                        Enabled
                                      </span>
                                    ) : (
                                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-800">
                                        Disabled
                                      </span>
                                    )}
                                  </div>
                                </div>
                                                                <button
                                  onClick={() => executeCronTask(task.id)}
                                  disabled={executing[task.id] || !task.enabled}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0"
                                >
                                  {executing[task.id] ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span className="text-xs font-medium">Running...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span className="text-xs font-medium">Run</span>
                                    </>
                                  )}
                                </button>
                              </div>
                              
                              {/* Description */}
                              <p className="text-xs text-gray-600 leading-snug">{task.description}</p>
                            
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Logs Tab */}
              {activeTab === 'logs' && (
                <div className="space-y-3">
                  {/* Compact Filters */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Search */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Search Logs</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search messages, operations..."
                            className="w-full px-3 py-1.5 pl-8 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>

                      {/* Level Filter */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Log Level</label>
                        <select
                          value={filterLevel}
                          onChange={(e) => setFilterLevel(e.target.value)}
                          className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">All Levels</option>
                          <option value="info">Info</option>
                          <option value="notice">Notice</option>
                          <option value="warning">Warning</option>
                          <option value="error">Error</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>

                    {/* Results count */}
                    <div className="mt-2 text-xs text-gray-600">
                      Showing {filteredLogs.length} of {logs.length} log entries
                    </div>
                  </div>

                  {/* Compact Log Entries */}
                  <div className="space-y-1.5">
                    {filteredLogs.length === 0 ? (
                      <div className="text-center py-8">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm text-gray-600 font-medium">No logs found</p>
                        <p className="text-xs text-gray-500 mt-1">Try adjusting your filters</p>
                      </div>
                    ) : (
                      filteredLogs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className="bg-white border border-gray-200 rounded-md p-2.5 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start gap-2">
                            {/* Level Badge */}
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded uppercase flex-shrink-0 ${getLevelBadge(log.level)}`}>
                              {log.level}
                            </span>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-xs font-medium text-gray-900 leading-tight">{log.message}</p>
                                <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                  {new Date(log.timestamp).toLocaleString()}
                                </span>
                              </div>

                              {/* Context Info */}
                              {log.context && (
                                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                  {log.context.operation && (
                                    <span className="text-xs text-gray-600">
                                      <span className="font-medium">Op:</span> {log.context.operation}
                                    </span>
                                  )}
                                  {log.context.task && (
                                    <span className="text-xs text-gray-600">
                                      <span className="font-medium">Task:</span> {log.context.task}
                                    </span>
                                  )}
                                  {log.context.module && (
                                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getModuleBadge(log.context.module)}`}>
                                      {log.context.module}
                                    </span>
                                  )}
                                  {log.context.duration_ms && (
                                    <span className="text-xs text-gray-600">
                                      ⏱️ {log.context.duration_ms}ms
                                    </span>
                                  )}
                                  {log.context.error && (
                                    <div className="w-full mt-1 p-1.5 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                                      <span className="font-medium">Error:</span> {log.context.error}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Compact Footer */}
        <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50 rounded-b-lg flex items-center justify-between">
          <div className="text-xs text-gray-600">
            <span className="font-medium">Last updated:</span> {new Date().toLocaleTimeString()}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCronInfo}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
            >
              <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-xs font-medium">Refresh</span>
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-xs font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CronManagement;
